import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import appLaunchIcon from "../assets/app-launch-icon.png";

const DEFAULT_MIN_SPLASH_MS = 2000;
const LEAVE_MS = 420;
const FAMILY_SWITCH_LEAVE_MS = 260;

/**
 * Uygulama içi başlangıç katmanı.
 * Açılıştaki StartupSplash ile aynı markup, animasyon ve süreyi kullanır.
 * `animationKey` değiştiğinde (manuel giriş gibi) animasyon sıfırdan başlar.
 */
const PremiumStartupSplash = ({
  visible,
  animationKey = 0,
  variant = "default",
  minimumDurationMs = DEFAULT_MIN_SPLASH_MS,
  canLeave = true,
  onVisualReady,
  onLeaveStart,
  onLeaveComplete,
}) => {
  const [leaving, setLeaving] = useState(false);
  const [minimumElapsed, setMinimumElapsed] = useState(false);
  const logoRef = useRef(null);
  const onVisualReadyRef = useRef(onVisualReady);
  const onLeaveStartRef = useRef(onLeaveStart);
  const onLeaveCompleteRef = useRef(onLeaveComplete);
  const familySwitch = variant === "family-switch";

  useEffect(() => {
    onVisualReadyRef.current = onVisualReady;
    onLeaveStartRef.current = onLeaveStart;
    onLeaveCompleteRef.current = onLeaveComplete;
  }, [onVisualReady, onLeaveStart, onLeaveComplete]);

  useEffect(() => {
    if (!visible || !onVisualReadyRef.current) return undefined;

    const logo = logoRef.current;
    if (!logo) return undefined;

    let cancelled = false;
    let firstFrame = null;
    let secondFrame = null;

    const reportAfterPaint = () => {
      if (cancelled) return;

      // Android 12+ native splash, Capacitor tarafında ilk window pre-draw'unu
      // SplashScreen.hide() çağrılana kadar bloke eder. Burada iki RAF beklemek
      // React çizimi ile native splash'in birbirini sonsuza dek beklemesine yol
      // açabilir. Görsel decode edildiğinde native perde kaldırılır; sıradaki ilk
      // Android karesinde hazır React logosu aynı konumda görünür.
      if (Capacitor.getPlatform() === "android") {
        onVisualReadyRef.current?.();
        return;
      }

      // iOS'ta native katman WebView çizimini engellemez. İki frame beklemek,
      // native logo kaldırılmadan React logosunun gerçekten boyandığını garanti eder.
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          if (!cancelled) onVisualReadyRef.current?.();
        });
      });
    };

    const prepareLogo = async () => {
      if (typeof logo.decode === "function") {
        try {
          await logo.decode();
        } catch (_) {
          // Yüklenmiş görsel bazı WebKit sürümlerinde decode reddedebilir.
        }
      }
      reportAfterPaint();
    };

    const handleLogoReady = () => {
      logo.removeEventListener("load", handleLogoReady);
      logo.removeEventListener("error", handleLogoReady);
      prepareLogo();
    };

    if (logo.complete) {
      prepareLogo();
    } else {
      logo.addEventListener("load", handleLogoReady);
      logo.addEventListener("error", handleLogoReady);
    }

    return () => {
      cancelled = true;
      logo.removeEventListener("load", handleLogoReady);
      logo.removeEventListener("error", handleLogoReady);
      if (firstFrame !== null) window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) window.cancelAnimationFrame(secondFrame);
    };
  }, [visible, animationKey]);

  useEffect(() => {
    if (!visible) {
      setLeaving(false);
      setMinimumElapsed(false);
      return undefined;
    }

    setLeaving(false);
    setMinimumElapsed(false);
    const timer = window.setTimeout(
      () => setMinimumElapsed(true),
      Math.max(0, Number(minimumDurationMs) || DEFAULT_MIN_SPLASH_MS)
    );
    return () => window.clearTimeout(timer);
  }, [visible, animationKey, minimumDurationMs]);

  useEffect(() => {
    if (!visible || !minimumElapsed || !canLeave || leaving) return;
    setLeaving(true);
  }, [visible, minimumElapsed, canLeave, leaving]);

  useEffect(() => {
    if (!leaving) return undefined;

    onLeaveStartRef.current?.();
    const timer = window.setTimeout(() => {
      onLeaveCompleteRef.current?.();
    }, familySwitch ? FAMILY_SWITCH_LEAVE_MS : LEAVE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving, familySwitch]);

  if (!visible) return null;

  return (
    <div
      key={animationKey}
      className={`startupSplash premiumStartupSplash${familySwitch ? " premiumStartupSplash--familySwitch" : ""}${leaving ? " startupSplash--leaving" : ""}`}
      aria-hidden="true"
      style={{
        "--startup-duration": `${minimumDurationMs}ms`,
        ...(leaving ? { animationDuration: `${familySwitch ? FAMILY_SWITCH_LEAVE_MS : LEAVE_MS}ms` } : {}),
      }}
    >
      <div className="startupSplash__aura startupSplash__aura--one" />
      <div className="startupSplash__aura startupSplash__aura--two" />
      <div className="startupSplash__particles" />
      <div className="startupSplash__logoShell">
        <div className="startupSplash__logoGlow" />
        <img ref={logoRef} src={appLaunchIcon} alt="" className="startupSplash__logo" draggable="false" />
      </div>
    </div>
  );
};

export default PremiumStartupSplash;
